import {
  TEMPLATE_KEYWORD_GENERATE_MAX_COUNT,
  type TemplateKeyword,
  type TemplateKeywordCategory,
  type TemplateKeywordGeneratePool,
} from "@adpilot/shared";
import { IconClock, IconPlus, IconSparkles } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogPopup, DialogTitle } from "@/components/ui/dialog";
import {
  NumberField,
  NumberFieldDecrement,
  NumberFieldGroup,
  NumberFieldIncrement,
  NumberFieldInput,
} from "@/components/ui/number-field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { resolveProductListings } from "@/lib/product-listing-api";
import { classifyBatches, estimateClassifyMinutes, KEYWORD_POOLS } from "./model";

const nf = (value: number) => value.toLocaleString("en-US");

interface ConfirmShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  children: React.ReactNode;
  footer: React.ReactNode;
}

/** TP-03 / TP-07 frame: 480px card, title block, body, table-footer action bar. */
function ConfirmShell({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: ConfirmShellProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        showCloseButton={false}
        className="w-[480px] max-w-[480px] rounded-[14px] bg-surface shadow-[0_24px_48px_#10182833]"
      >
        <div className="flex flex-col gap-1.5 px-5 pt-5">
          <DialogTitle className="text-base font-semibold leading-6 text-ink">{title}</DialogTitle>
          <p className="text-[13px] leading-5 text-text-secondary">{description}</p>
        </div>
        <div className="flex flex-col gap-4 px-5 pt-4 pb-5">{children}</div>
        <div className="flex justify-end gap-2 rounded-b-[14px] border-t border-line bg-table-footer px-5 py-3">
          {footer}
        </div>
      </DialogPopup>
    </Dialog>
  );
}

function InfoBox({ title, rows }: { title: string; rows: { label: string; value: string }[] }) {
  return (
    <div className="flex flex-col gap-2 rounded-[10px] border border-line bg-canvas p-3">
      <span className="text-xs font-medium text-ink">{title}</span>
      <div className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between gap-4 text-xs">
            <span className="shrink-0 text-text-secondary">{row.label}</span>
            <span className="text-right text-ink">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Estimate({ text }: { text: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <IconClock className="size-3.5 shrink-0" aria-hidden />
      {text}
    </span>
  );
}

const cancelClassName = "h-9 rounded-lg border-line bg-surface px-3 text-xs text-ink";
const confirmClassName = "h-9 rounded-lg px-3 text-xs";

export function ClassifyDialog({
  open,
  onOpenChange,
  pendingCount,
  starting,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingCount: number;
  starting: boolean;
  onConfirm: () => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  const minutes = estimateClassifyMinutes(classifyBatches(pendingCount));
  return (
    <ConfirmShell
      open={open}
      onOpenChange={onOpenChange}
      title={t("keywords.classify.title")}
      description={t("keywords.classify.description")}
      footer={
        <>
          <Button variant="outline" className={cancelClassName} onClick={() => onOpenChange(false)}>
            {t("actions.cancel")}
          </Button>
          <Button className={confirmClassName} loading={starting} onClick={onConfirm}>
            <IconSparkles className="size-3.5" aria-hidden />
            {t("keywords.classify.start")}
          </Button>
        </>
      }
    >
      <InfoBox
        title={t("keywords.classify.task")}
        rows={[
          {
            label: t("keywords.classify.pending"),
            value: t("keywords.count", { count: pendingCount, value: nf(pendingCount) }),
          },
        ]}
      />
      <Estimate text={t("keywords.classify.estimate", { count: minutes })} />
    </ConfirmShell>
  );
}

const DEFAULT_GENERATE_COUNT = 100;

export function GenerateDialog({
  pool,
  onOpenChange,
  shopId,
  listingKey,
  keywords,
  starting,
  onConfirm,
}: {
  /** null = closed. */
  pool: TemplateKeywordGeneratePool | null;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  /** The draft product's catalog key (a seller's SKU, a vendor's ASIN); null before ① is saved. */
  listingKey: string | null;
  keywords: readonly TemplateKeyword[];
  starting: boolean;
  onConfirm: (pool: TemplateKeywordGeneratePool, count: number) => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  const [count, setCount] = useState(DEFAULT_GENERATE_COUNT);
  useEffect(() => {
    if (pool) setCount(DEFAULT_GENERATE_COUNT);
  }, [pool]);
  // Keeps the copy while the dialog animates out.
  const lastPool = useRef(pool);
  if (pool) lastPool.current = pool;
  const shown = lastPool.current;
  const product = useQuery({
    queryKey: ["template-portfolio", "product-title", shopId, listingKey],
    queryFn: () => resolveProductListings(shopId, [listingKey as string]),
    enabled: pool === "negative" && Boolean(listingKey),
    staleTime: 5 * 60_000,
  });
  const resolved = product.data?.results[0];
  const title = resolved && "listing" in resolved ? resolved.listing.title : null;

  const pick = (category: TemplateKeywordCategory) =>
    keywords.filter((keyword) => keyword.category === category && keyword.selected);
  const core = pick("core");
  const longTail = pick("long_tail");
  const rows = [
    {
      label: t("keywords.pools.core"),
      value:
        core.length > 0
          ? t("keywords.generate.coreValue", {
              count: core.length,
              examples: core
                .slice(0, 2)
                .map((keyword) => keyword.text)
                .join("、"),
            })
          : t("keywords.count", { count: 0, value: "0" }),
    },
    {
      label: t("keywords.pools.long_tail"),
      value: t("keywords.count", { count: longTail.length, value: nf(longTail.length) }),
    },
  ];
  if (shown === "competitor_brand") {
    const existing = pick("competitor_brand").length;
    rows.push({
      label: t("keywords.pools.competitor_brand"),
      value: t("keywords.count", { count: existing, value: nf(existing) }),
    });
  }
  if (shown === "negative" && title) {
    rows.push({ label: t("keywords.generate.productTitle"), value: title });
  }

  return (
    <ConfirmShell
      open={pool !== null}
      onOpenChange={onOpenChange}
      title={shown ? t("keywords.generate.title", { pool: t(`keywords.pools.${shown}`) }) : ""}
      description={shown ? t(`keywords.generate.description.${shown}`) : ""}
      footer={
        <>
          <Button variant="outline" className={cancelClassName} onClick={() => onOpenChange(false)}>
            {t("actions.cancel")}
          </Button>
          <Button
            className={confirmClassName}
            loading={starting}
            onClick={() => pool && onConfirm(pool, count)}
          >
            <IconSparkles className="size-3.5" aria-hidden />
            {t("keywords.generate.submit")}
          </Button>
        </>
      }
    >
      <InfoBox title={t("keywords.generate.reference")} rows={rows} />
      <div className="flex items-end justify-between gap-4">
        <NumberField
          className="w-[140px] gap-1.5"
          value={count}
          min={1}
          max={TEMPLATE_KEYWORD_GENERATE_MAX_COUNT}
          step={10}
          onValueChange={(value) => setCount(value ?? DEFAULT_GENERATE_COUNT)}
        >
          <span className="text-xs font-medium text-ink">{t("keywords.generate.count")}</span>
          <NumberFieldGroup className="h-9 overflow-clip rounded-lg">
            <NumberFieldDecrement className="w-8 border-r border-line px-0" />
            <NumberFieldInput className="font-mono text-[13px]" />
            <NumberFieldIncrement className="w-8 border-l border-line px-0" />
          </NumberFieldGroup>
        </NumberField>
        <span className="pb-[22px]">
          <Estimate text={t("keywords.generate.estimate")} />
        </span>
      </div>
    </ConfirmShell>
  );
}

export function AddKeywordsDialog({
  open,
  onOpenChange,
  defaultCategory,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCategory: TemplateKeywordCategory;
  onAdd: (input: string, category: TemplateKeywordCategory) => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  const [input, setInput] = useState("");
  const [category, setCategory] = useState<TemplateKeywordCategory>(defaultCategory);
  const items = KEYWORD_POOLS.map((pool) => ({ value: pool, label: t(`keywords.pools.${pool}`) }));
  useEffect(() => {
    if (!open) return;
    setInput("");
    setCategory(defaultCategory);
  }, [open, defaultCategory]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        showCloseButton={false}
        className="w-[480px] max-w-[480px] overflow-clip rounded-xl bg-surface shadow-[0_24px_48px_#0F172A2E]"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onAdd(input, category);
          }}
        >
          <div className="flex flex-col gap-4 p-5">
            <div className="flex flex-col gap-1">
              <DialogTitle className="text-base font-semibold leading-6 text-ink">
                {t("keywords.add.title")}
              </DialogTitle>
              <p className="text-[13px] leading-5 text-text-secondary">
                {t("keywords.add.description")}
              </p>
            </div>
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium leading-4 text-ink">
                {t("keywords.add.keywords")}
              </span>
              <textarea
                autoFocus
                value={input}
                onChange={(event) => setInput(event.target.value)}
                className="h-24 resize-none rounded-lg border border-control-border bg-surface px-3 py-2.5 text-sm leading-5 text-ink outline-none transition-[border-color,box-shadow] focus:border-primary focus:shadow-[0_0_0_3px_var(--color-brand-3)]"
              />
            </label>
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium leading-4 text-ink">
                {t("keywords.add.category")}
              </span>
              <Select
                items={items}
                value={category}
                onValueChange={(value) => value && setCategory(value)}
              >
                <SelectTrigger
                  className="h-9 w-full rounded-lg border-line bg-surface px-3 text-sm"
                  aria-label={t("keywords.add.category")}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {items.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-line bg-canvas px-5 py-3">
            <Button
              variant="outline"
              className="h-9 rounded-lg border-line bg-surface px-3 text-[13px] text-ink"
              onClick={() => onOpenChange(false)}
            >
              {t("actions.cancel")}
            </Button>
            <Button type="submit" className="h-9 rounded-lg px-3 text-[13px]">
              <IconPlus className="size-3.5" aria-hidden />
              {t("keywords.add.submit")}
            </Button>
          </div>
        </form>
      </DialogPopup>
    </Dialog>
  );
}
